import {
  ProfilesApiFactory as AdminProfilesApiFactory,
  TokensApiFactory as AdminTokensApiFactory,
  CommunitiesApiFactory as CerebroCommunitiesApiFactory,
  VersionsApiFactory,
} from '@sunrise-ui/api/cerebro';
import {
  EventsApiFactory,
  GramsApiFactory,
  LedgerApiFactory,
  PromosApiFactory,
  RoomsApiFactory,
  SessionsApiFactory,
  TicketsApiFactory,
} from '@sunrise-ui/api/events';
import {
  AuctionsApiFactory,
  ExtensionsApiFactory,
  PaymentsApiFactory,
  TokensApiFactory,
} from '@sunrise-ui/api/gamification';
import { FormsApiFactory, LinksApiFactory } from '@sunrise-ui/api/insights';
import {
  Configuration,
  ConversationsApiFactory,
  InboxApiFactory,
} from '@sunrise-ui/api/messenger';
import {
  ConnectionsApiFactory,
  FollowsApiFactory,
  IntroductionsApiFactory,
  MembershipsApiFactory,
  PeopleApiFactory,
  SubcommunitiesApiFactory,
} from '@sunrise-ui/api/network';
import { EventsApiFactory as NotificationsApiFactory } from '@sunrise-ui/api/notifications';
import {
  CommunitiesApiFactory,
  ProfilesApiFactory,
} from '@sunrise-ui/api/profile';
import {
  AuthFlowError,
  CustomerIO,
  authenticateUser,
  useAuthStore,
} from '@sunrise-ui/primitives';
import type {
  DefaultError,
  InfiniteData,
  NoInfer,
  QueryKey,
} from '@tanstack/query-core';
import type {
  QueryClient,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  CEREBRO_BASE_URL,
  EVENTS_BASE_URL,
  GAMIFICATION_BASE_URL,
  INSIGHTS_BASE_URL,
  MESSENGER_BASE_URL,
  NETWORK_BASE_URL,
  NOTIFICATIONS_BASE_URL,
  PROFILE_BASE_URL,
} from './env';

let refreshPromise: Promise<void> | undefined;
let queryClient: QueryClient;

/**
 * Register the query client for access in api layer
 * Used at logout to clear the cache
 */
export function registerQueryClient(client: QueryClient) {
  queryClient = client;
}

/**
 * Uses the auth store to conditionally enable queries
 */
export function useAuthQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({
  enabled,
  ...otherProps
}: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<
  NoInfer<TData>,
  TError
> {
  const { isAuthenticated } = useAuthStore();
  return useQuery({ enabled: isAuthenticated && enabled, ...otherProps });
}

/**
 * Uses the auth store to conditionally enable infinite queries
 */
export function useAuthInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>({
  enabled,
  ...otherProps
}: UseInfiniteQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey,
  TPageParam
>): UseInfiniteQueryResult<TData, TError> {
  const { isAuthenticated } = useAuthStore();
  return useInfiniteQuery({
    enabled: isAuthenticated && enabled,
    ...otherProps,
  });
}

/**
 * Wraps useMutation with authentication and onboarding checks
 * Opens auth/onboarding modal if user is not authenticated or not onboarded when mutation is triggered
 *
 * If user is not authenticated or not onboarded:
 * - Keeps mutation in loading state (not error state)
 * - Opens appropriate modal (auth or onboarding) and waits for user to complete
 * - On successful completion: proceeds with mutation through React Query lifecycle
 * - On dismiss: throws error to reject the mutation
 *
 * This preserves React Query's lifecycle including onSuccess callbacks and cache invalidation.
 */
export function useAuthMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  return useMutation({
    ...options,
    mutationFn: async (variables: TVariables) => {
      const { hasCheckedAuth } = useAuthStore.getState();

      if (!hasCheckedAuth) {
        console.warn('Attempted action before auth checks have finished');
        throw new AuthFlowError(
          'Attempted action before auth checks have finished',
        );
      }

      await authenticateUser();

      if (!options.mutationFn) {
        throw new Error('mutationFn is required');
      }

      return options.mutationFn(variables);
    },
    onError: (error, variables, context) => {
      // Note: this will still mean mutateAsync throws the error
      if (error instanceof AuthFlowError) {
        return;
      }

      options.onError?.(error, variables, context);
    },
  });
}

export async function logout() {
  const state = useAuthStore.getState();

  state?.clearAuth();

  queryClient?.clear();
  if (CustomerIO) {
    await CustomerIO.clearIdentify();
  }
}

/**
 * Refresh the access token using the refresh token
 * Deduplicated - multiple simultaneous calls will share the same refresh request
 * @param overrideRefreshToken - Optional refresh token to use instead of reading from store
 * Updates the auth store with new tokens on success
 */
export const refreshToken = async (overrideRefreshToken?: string) => {
  // If already refreshing, wait for the existing promise
  if (refreshPromise) {
    await refreshPromise;
    return;
  }

  // Start the refresh process
  refreshPromise = (async () => {
    const state = useAuthStore.getState();

    const { refreshToken, setAuthData } = state;
    const refreshTokenValue = overrideRefreshToken ?? refreshToken;

    // If refresh token is empty, clear auth and show login modal
    if (!refreshTokenValue) {
      await logout();
      throw new Error(`Refresh token is missing`);
    }

    const { access_token, refresh_token, user_id } = await tokensApi
      .refreshAccessTokenTokensRefreshPost({
        refreshAccessTokenInput: {
          refresh_token: refreshTokenValue,
        },
      })
      .then((response) => response.data);

    // Update auth store with new tokens
    await setAuthData({
      authToken: access_token,
      refreshToken: refresh_token,
      currentUserId: user_id,
    });
  })();

  // Wait for the refresh to complete
  try {
    await refreshPromise;
  } finally {
    // Clear the promise AFTER all awaiting calls have resolved
    // This prevents a race condition where the promise is cleared while other
    // requests are still waiting, which could allow a new refresh to start prematurely
    refreshPromise = undefined;
  }
};

const responseErrorHandler =
  (axiosInstance: AxiosInstance) => async (error: AxiosError) => {
    if (error.config?.url?.includes('/tokens/refresh')) {
      // Token refresh failed - clear auth and show login modal
      await logout();
      throw error;
    } else if (error.response?.status === 401) {
      // Check if we've already attempted to refresh for this request
      const config = error.config as {
        __retryAttempted?: boolean;
      };

      if (config?.__retryAttempted) {
        // Already attempted refresh once - logout to prevent infinite loop
        // This can happen if refresh returns an invalid token (server bug)
        // or if user's session is invalid for other reasons
        await logout();
        throw new Error(
          'Token refresh retry failed - possible invalid session or server issue',
        );
      }

      // Attempt to refresh token (deduplicated automatically)
      await refreshToken();

      // Get the fresh token from store
      const state = useAuthStore.getState();

      const { authToken } = state;

      // Ensure we have a fresh token for retry
      if (!authToken) {
        throw new Error('Failed to obtain fresh auth token');
      }

      // Clone the config and set the fresh token directly
      const retryConfig = { ...error.config } as InternalAxiosRequestConfig & {
        __retryAttempted?: boolean;
      };
      if (retryConfig.headers) {
        retryConfig.headers.Authorization = `Bearer ${authToken}`;
      }

      // Mark that we've attempted a retry to prevent infinite loops
      retryConfig.__retryAttempted = true;

      // Retry the request with the new token
      return axiosInstance.request(retryConfig);
    }

    throw error;
  };

const requestFulfilledHandler = async (config: InternalAxiosRequestConfig) => {
  const state = useAuthStore.getState();

  const { authToken } = state || {};
  if (authToken) config.headers.Authorization = `Bearer ${authToken}`;

  return config;
};

function createAxiosApi<T>(
  factory: (
    configuration?: Configuration,
    basePath?: string,
    axios?: AxiosInstance,
  ) => T,
  baseURL?: string,
) {
  const configuration = new Configuration();

  const axiosInstance = axios.create({
    baseURL,
  });

  axiosInstance.interceptors.request.use(
    requestFulfilledHandler,
    async (error) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    responseErrorHandler(axiosInstance),
  );

  return factory(configuration, baseURL, axiosInstance);
}

export const profilesApi = createAxiosApi(ProfilesApiFactory, PROFILE_BASE_URL);
export const communitiesApi = createAxiosApi(
  CommunitiesApiFactory,
  PROFILE_BASE_URL,
);
export const connectionsApi = createAxiosApi(
  ConnectionsApiFactory,
  NETWORK_BASE_URL,
);
export const introductionsApi = createAxiosApi(
  IntroductionsApiFactory,
  NETWORK_BASE_URL,
);
export const peopleApi = createAxiosApi(PeopleApiFactory, NETWORK_BASE_URL);

export const followsApi = createAxiosApi(FollowsApiFactory, NETWORK_BASE_URL);
export const subcommunitiesApi = createAxiosApi(
  SubcommunitiesApiFactory,
  NETWORK_BASE_URL,
);
export const membershipsApi = createAxiosApi(
  MembershipsApiFactory,
  NETWORK_BASE_URL,
);
export const eventsApi = createAxiosApi(EventsApiFactory, EVENTS_BASE_URL);
export const promosApi = createAxiosApi(PromosApiFactory, EVENTS_BASE_URL);
export const ticketsApi = createAxiosApi(TicketsApiFactory, EVENTS_BASE_URL);
export const roomsApi = createAxiosApi(RoomsApiFactory, EVENTS_BASE_URL);
export const sessionsApi = createAxiosApi(SessionsApiFactory, EVENTS_BASE_URL);
export const gramsApi = createAxiosApi(GramsApiFactory, EVENTS_BASE_URL);
export const notificationsApi = createAxiosApi(
  NotificationsApiFactory,
  NOTIFICATIONS_BASE_URL,
);
export const conversationsApi = createAxiosApi(
  ConversationsApiFactory,
  MESSENGER_BASE_URL,
);
export const inboxApi = createAxiosApi(InboxApiFactory, MESSENGER_BASE_URL);
export const tokensApi = createAxiosApi(
  AdminTokensApiFactory,
  CEREBRO_BASE_URL,
);
export const versionsApi = createAxiosApi(VersionsApiFactory, CEREBRO_BASE_URL);
export const adminProfilesApi = createAxiosApi(
  AdminProfilesApiFactory,
  CEREBRO_BASE_URL,
);
export const cerebroCommunitiesApi = createAxiosApi(
  CerebroCommunitiesApiFactory,
  CEREBRO_BASE_URL,
);
export const gamificationApi = createAxiosApi(
  TokensApiFactory,
  GAMIFICATION_BASE_URL,
);

export const extensionsApi = createAxiosApi(
  ExtensionsApiFactory,
  GAMIFICATION_BASE_URL,
);

export const auctionsApi = createAxiosApi(
  AuctionsApiFactory,
  GAMIFICATION_BASE_URL,
);
export const paymentsApi = createAxiosApi(
  PaymentsApiFactory,
  GAMIFICATION_BASE_URL,
);
export const linksApi = createAxiosApi(LinksApiFactory, INSIGHTS_BASE_URL);
export const formsApi = createAxiosApi(FormsApiFactory, INSIGHTS_BASE_URL);
export const ledgerApi = createAxiosApi(LedgerApiFactory, EVENTS_BASE_URL);
